import { apiFetch } from "../utils/apiFetch";

const VIDEO_API = process.env.REACT_APP_VIDEO_API_URL;

if (!VIDEO_API) {
  console.error("Video API URL missing — check REACT_APP_VIDEO_API_URL");
}

export class VideoApiError extends Error {
  constructor(code, message, { status, quota } = {}) {
    super(message);
    this.name = "VideoApiError";
    this.code = code;
    this.status = status;
    this.quota = quota;
  }
}

const isAbort = (err) => err?.name === "AbortError";

// Network failures become VideoApiError("NETWORK"); aborts pass through untouched.
// The message never includes the URL because upload URLs carry a SAS token.
const safeFetch = async (url, options) => {
  try {
    // apiFetch adds the token only for Feelim API hosts; SAS URLs (Blob) go out untouched
    return await apiFetch(url, options);
  } catch (err) {
    if (isAbort(err)) throw err;
    throw new VideoApiError("NETWORK", "Network error");
  }
};

// Errors from our API always come as { error: CODE, message }.
const throwApiError = async (res) => {
  let body = {};
  try {
    body = await res.json();
  } catch {
    /* not JSON */
  }
  throw new VideoApiError(
    body.error || "REQUEST_FAILED",
    body.message || `Request failed (${res.status})`,
    { status: res.status, quota: body.quota }
  );
};

const callApi = async (path, { method = "GET", body, signal } = {}) => {
  const res = await safeFetch(`${VIDEO_API}${path}`, {
    method,
    signal,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) await throwApiError(res);
  return res.json().catch(() => null);
};

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(id);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });

const withRetry = async (fn, { signal, attempts = 3, delay = 1000, shouldRetry = () => true } = {}) => {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      if (isAbort(err)) throw err;
      lastError = err;
      if (!shouldRetry(err) || i === attempts - 1) break;
      await wait(delay * (i + 1), signal);
    }
  }
  throw lastError;
};

// PUT to Azure Blob with a SAS URL. Failures carry no URL in the message.
const putToBlob = async (url, options) => {
  const res = await safeFetch(url, { method: "PUT", ...options });
  if (!res.ok) throw new VideoApiError("UPLOAD_FAILED", "Upload failed", { status: res.status });
  return res;
};

// API 1 — consumes 1 unit of quota. The user comes from the access token.
export const createUploadSession = ({ plan, target, file, duration }, { signal } = {}) =>
  callApi("/video/upload-session", {
    method: "POST",
    signal,
    body: {
      plan,
      target,
      filename: file.name,
      content_type: "video/mp4",
      size_bytes: file.size,
      duration_seconds: duration,
    },
  });

// API 2
export const uploadPoster = (session, posterBlob, { signal } = {}) =>
  withRetry(
    () =>
      putToBlob(session.poster_upload_url, {
        headers: session.poster_upload_headers,
        body: posterBlob,
        signal,
      }),
    { signal }
  );

// API 3 — blocks, then the block list. Progress goes from 0 to 1.
export const uploadVideoBlocks = async (session, file, { onProgress, signal } = {}) => {
  const total = Math.ceil(file.size / session.block_size);
  const ids = [];

  for (let i = 0; i < total; i++) {
    const id = btoa(String(i).padStart(6, "0")); // all block ids must have the same length
    ids.push(id);
    await withRetry(
      () =>
        putToBlob(`${session.upload_url}&comp=block&blockid=${encodeURIComponent(id)}`, {
          body: file.slice(i * session.block_size, (i + 1) * session.block_size),
          signal,
        }),
      { signal }
    );
    onProgress?.((i + 1) / total);
  }

  const xml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${ids
    .map((x) => `<Latest>${x}</Latest>`)
    .join("")}</BlockList>`;

  await withRetry(
    () =>
      putToBlob(`${session.upload_url}&comp=blocklist`, {
        headers: session.put_block_list_headers,
        body: xml,
        signal,
      }),
    { signal }
  );
};

// API 4 — returns { video_id, status, blob_url, poster_url }
export const completeUpload = (email, videoId, { signal } = {}) =>
  callApi("/video/complete", {
    method: "POST",
    signal,
    body: { video_id: videoId },
  });

// API 6 — idempotent, so retry on network/server errors only.
export const attachVideo = (email, videoId, postId, { signal } = {}) =>
  withRetry(
    () =>
      callApi("/video/attach", {
        method: "POST",
        signal,
        body: { video_id: videoId, post_id: postId },
      }),
    {
      signal,
      shouldRetry: (err) => err.code === "NETWORK" || err.status >= 500,
    }
  );

// API 7 — frees the quota if the video was not published. 409 VIDEO_BUSY while LinkedIn is publishing.
export const deleteVideo = (email, videoId, { signal } = {}) =>
  callApi(`/video?video_id=${encodeURIComponent(videoId)}`, 
    { 
      method: "DELETE", signal 
    });

// API 8 — UPLOADING, UPLOADED, LI_UPLOADING, LI_PROCESSING, PUBLISHED, FAILED
export const getVideoStatus = (email, videoId, { signal } = {}) =>
  callApi(`/video/status?video_id=${encodeURIComponent(videoId)}`, 
    { 
      signal 
    });